import { apiClient } from '../api';
import { Path } from '../api/backendUrl';
import { logError } from '../utils/logger';
import { passwordEncrypt } from '../utils/passwordEncrypt';

const userApiPath = import.meta.env.VITE_APP_BACKEND_USER;

export interface ChangePasswordPayload {
  currentPassword: string;
  newPassword: string;
  changePasswordToken: string;
}

export interface VerifyChangePasswordOtpPayload {
  otp?: string;
  totpCode?: string;
  securityToken?: string;
}

export interface ChangePasswordVerificationInfo {
  verificationType: 'email' | 'mfa';
  mfaEnabled: boolean;
  securityToken?: string;
  maskedEmail?: string;
  expiresInSeconds?: number;
  resumed?: boolean;
  message?: string;
}

export interface VerifyChangePasswordOtpResult {
  changePasswordToken: string;
  verificationType: 'email' | 'mfa';
  expiresInSeconds: number;
}

export const requestChangePasswordOtp = async () => {
  try {
    const res = await apiClient.post(
      `${userApiPath}${Path.changePasswordRequestOtp}`
    );
    return res.data as {
      success: boolean;
      message: string;
      data: ChangePasswordVerificationInfo;
    };
  } catch (error: unknown) {
    logError('ERROR request change password OTP', { error });
    throw error;
  }
};

export const resendChangePasswordOtp = async () => {
  try {
    const res = await apiClient.post(
      `${userApiPath}${Path.changePasswordResendOtp}`
    );
    return res.data as {
      success: boolean;
      message: string;
      data: ChangePasswordVerificationInfo;
    };
  } catch (error: unknown) {
    logError('ERROR resend change password OTP', { error });
    throw error;
  }
};

export const verifyChangePasswordOtp = async (
  data: VerifyChangePasswordOtpPayload
) => {
  try {
    const res = await apiClient.post(
      `${userApiPath}${Path.changePasswordVerifyOtp}`,
      data
    );
    return res.data as {
      success: boolean;
      message: string;
      data: VerifyChangePasswordOtpResult;
    };
  } catch (error: unknown) {
    logError('ERROR verify change password OTP', { error });
    throw error;
  }
};

export const changePassword = async (data: ChangePasswordPayload) => {
  try {
    const encryptedCurrent = passwordEncrypt(data.currentPassword);
    const encryptedNew = passwordEncrypt(data.newPassword);

    if (!encryptedCurrent || !encryptedNew) {
      throw new Error('Unable to encrypt password. Please try again.');
    }

    const res = await apiClient.post(`${userApiPath}${Path.changePassword}`, {
      currentPassword: encryptedCurrent,
      newPassword: encryptedNew,
      changePasswordToken: data.changePasswordToken,
    });
    return res.data as { success: boolean; message: string };
  } catch (error: unknown) {
    logError('ERROR change password', { error });
    throw error;
  }
};
